/**
 * VWAP (Volume Weighted Average Price) Indicator
 * Displays VWAP line with optional standard deviation bands.
 *
 * Session modes:
 *   'full'  — single full-Globex-session VWAP (anchored 6 PM ET prev day)
 *   'split' — full-session VWAP + an RTH-anchored VWAP overlay (9:30-16:00 ET)
 *
 * Each session is rendered as a "set": one VWAP line + four band series.
 * The backend serves per-session data (session_type 'eth' | 'rth') and SSE
 * payloads carry session_type, which routes updates to the right set.
 *
 * Real-time updates via SSE (Server-Sent Events) for today's data.
 * Historical multi-day data fetched on-demand via REST.
 */

import { CONFIG } from '../config';
import { LineSeries } from 'lightweight-charts';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

const SESSION_STYLES = {
  eth: { color: '#2962FF', band1: 'rgba(41, 98, 255, 0.3)', band2: 'rgba(41, 98, 255, 0.2)', title: 'VWAP' },
  rth: { color: '#FF9800', band1: 'rgba(255, 152, 0, 0.3)', band2: 'rgba(255, 152, 0, 0.2)', title: 'RTH VWAP' },
};

export class VWAPIndicator {
  constructor(chart, series) {
    this.chart = chart;
    this.candleSeries = series;
    this.enabled = false;
    this.sessionMode = 'full';   // 'full' (eth only) or 'split' (eth + rth)
    this.showBand1 = false;
    this.showBand2 = false;
    this.isMultiDay = false;

    // Renko alignment: an ascending array of brick "slot" seconds (the chart's
    // de-collided brick times). When set, VWAP points are re-stamped onto these
    // slots so the VWAP time set is a strict subset of the brick times — otherwise
    // lightweight-charts adds the VWAP timestamps as extra bar indices and the
    // bricks space out. null on every non-renko chart (alignment is inert).
    this.renkoSlots = null;

    // Per-session state: { eth: {...}, rth: {...} }
    this.sets = {};         // session -> {line, b1u, b1l, b2u, b2l}
    this.sessionData = {};  // session -> {vwap, vwap_data: [...]} (combined for multi-day)
    this.sessionByDay = {}; // session -> {date -> day data} (multi-day mode)

    // SSE unsubscribe functions for real-time updates
    this._sseUnsubs = [];

    // Stored params for updates
    this.currentSymbol = null;
    this.currentDate = null;
    this.startDate = null;
    this.endDate = null;
  }

  /** Sessions that should be visible for the current mode. */
  _activeSessions() {
    return this.sessionMode === 'split' ? ['eth', 'rth'] : ['eth'];
  }

  /**
   * Set session mode ('full' | 'split'); refetches and redraws when changed.
   */
  async setSessionMode(mode) {
    const normalized = mode === 'split' ? 'split' : 'full';
    if (normalized === this.sessionMode) return;
    this.sessionMode = normalized;
    if (this.enabled) {
      await this._fetchAll();
      this.draw();
    }
  }

  /**
   * Fetch VWAP data for one session (single day)
   */
  async fetchData(symbol, currentDate, timeframe = '5m', sessionType = 'eth') {
    try {
      const url = `${CONFIG.apiUrl}/api/indicators/vwap?symbol=${symbol}&start_date=${currentDate}&timeframe=${timeframe}&session_type=${sessionType}`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch VWAP data: ${response.statusText}`);
      }
      const data = await response.json();
      console.log(`VWAPIndicator: ${sessionType} data`, {
        vwap: data.vwap,
        points: data.vwap_data?.length || 0
      });
      this.sessionData[sessionType] = data;
      return data;
    } catch (error) {
      console.error(`VWAPIndicator: Error fetching ${sessionType} data:`, error);
      return null;
    }
  }

  /**
   * Fetch multi-day VWAP data for one session
   */
  async fetchMultiDayData(symbol, startDate, endDate, timeframe = '5m', sessionType = 'eth') {
    try {
      const url = `${CONFIG.apiUrl}/api/indicators/vwap?symbol=${symbol}&start_date=${startDate}&end_date=${endDate}&timeframe=${timeframe}&session_type=${sessionType}`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch multi-day VWAP data: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.vwap_by_day) {
        this.sessionByDay[sessionType] = data.vwap_by_day;
        this.sessionData[sessionType] = this._combineMultiDayData(data.vwap_by_day);
        console.log(`VWAPIndicator: multi-day ${sessionType} data for ${Object.keys(data.vwap_by_day).length} days`);
      }
      return this.sessionData[sessionType];
    } catch (error) {
      console.error(`VWAPIndicator: Error fetching multi-day ${sessionType} data:`, error);
      return null;
    }
  }

  /** Fetch all active sessions for the current date range. */
  async _fetchAll() {
    const sessions = this._activeSessions();
    if (this.isMultiDay) {
      await Promise.all(sessions.map(st =>
        this.fetchMultiDayData(this.currentSymbol, this.startDate, this.endDate, this.timeframe, st)
      ));
    } else {
      await Promise.all(sessions.map(st =>
        this.fetchData(this.currentSymbol, this.startDate, this.timeframe, st)
      ));
    }
  }

  /**
   * Combine multi-day VWAP data into a single chronological dataset.
   *
   * A `gap` marker is inserted between days; series builders render it as a
   * whitespace point so lightweight-charts breaks the line instead of drawing
   * a connecting segment across the session gap (egregious for RTH, whose
   * 16:00 close would otherwise connect straight to the next 9:30 open).
   */
  _combineMultiDayData(vwapByDay) {
    const allVwapData = [];
    const sortedDays = Object.keys(vwapByDay).sort();

    for (const dayStr of sortedDays) {
      const dayData = vwapByDay[dayStr];
      if (dayData && dayData.vwap_data && dayData.vwap_data.length > 0) {
        const dayPoints = [...dayData.vwap_data].sort((a, b) => a.time - b.time);
        if (allVwapData.length > 0) {
          allVwapData.push({ time: allVwapData[allVwapData.length - 1].time + 1, gap: true });
        }
        allVwapData.push(...dayPoints);
      }
    }

    const lastPoint = allVwapData.length > 0 ? allVwapData[allVwapData.length - 1] : null;

    return {
      vwap_data: allVwapData,
      vwap: lastPoint ? lastPoint.vwap : 0,
      source: 'multi_day'
    };
  }

  /**
   * Map points to series data; `gap` markers become whitespace (time only),
   * which breaks the line at that position.
   */
  _toSeriesData(points, valueOf) {
    return points.map(p => (p.gap ? { time: p.time } : { time: p.time, value: valueOf(p) }));
  }

  /**
   * Sample VWAP points to the current candle timeframe.
   * Mirrors the REST API logic: group by interval, keep the last point per
   * interval, snap timestamp to interval boundary.
   */
  _sampleToTimeframe(points) {
    const tfMap = { '1s': 1, '5s': 5, '15s': 15, '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600 };
    const sampleSeconds = tfMap[this.timeframe] || 300;

    const sampled = [];
    let lastInterval = null;

    for (const point of points) {
      const currentInterval = Math.floor(point.time / sampleSeconds);
      if (lastInterval !== currentInterval) {
        sampled.push({ ...point, time: currentInterval * sampleSeconds });
        lastInterval = currentInterval;
      } else {
        sampled[sampled.length - 1] = { ...point, time: currentInterval * sampleSeconds };
      }
    }

    return sampled;
  }

  /**
   * Set the renko brick slots used to align VWAP onto the brick time axis.
   * Pass the ascending array of brick `time` values (the chart's de-collided
   * slots), or null/empty to disable alignment (every non-renko chart).
   */
  setRenkoSlots(slots) {
    this.renkoSlots = (Array.isArray(slots) && slots.length)
      ? slots.slice().sort((a, b) => a - b)
      : null;
  }

  /**
   * Re-stamp VWAP points onto the renko brick slots so the VWAP time set is a
   * strict, increasing subset of the brick times (no extra bar indices -> no
   * brick spacing). Each point (real or `gap` marker) is placed on the latest
   * slot <= its time (causal: never show a future VWAP on an earlier brick);
   * points before the first slot are dropped, and points landing on the same
   * slot collapse to the last one. `gap` markers are kept so the line still
   * breaks across overnight gaps on multi-day views (a gap maps onto the prior
   * day's last slot and, by keep-last, becomes the break there).
   */
  _alignToSlots(points) {
    const slots = this.renkoSlots;
    if (!slots || slots.length === 0) return points;

    const sorted = points.slice().sort((a, b) => a.time - b.time);
    const out = [];
    let si = 0;            // monotonic pointer into slots
    let lastSlot = null;
    for (const p of sorted) {
      while (si + 1 < slots.length && slots[si + 1] <= p.time) si++;
      if (slots[si] > p.time) continue;   // before the first brick -> drop
      const slot = slots[si];
      const mapped = { ...p, time: slot };
      if (lastSlot === slot) {
        out[out.length - 1] = mapped;      // keep the last sample for this slot
      } else {
        out.push(mapped);
        lastSlot = slot;
      }
    }
    return out;
  }

  /** Points to render for a session: renko-aligned when slots are set, else raw. */
  _renderPoints(data) {
    const pts = (data && data.vwap_data) ? data.vwap_data : [];
    return this.renkoSlots ? this._alignToSlots(pts) : pts;
  }

  /**
   * Handle VWAP update from SSE (snapshot or live).
   * Payloads carry meta.session_type ('eth' | 'rth') and meta.date (the
   * session date); updates not relevant to the current mode are ignored.
   */
  _handleVwapUpdate(data) {
    if (!this.enabled || !data?.meta) return;

    const sessionType = data.meta.session_type;
    if (!this._activeSessions().includes(sessionType)) return;

    // SSE payloads are unsampled; sample to the candle timeframe so VWAP
    // timestamps align with candle timestamps
    const sampledData = this._sampleToTimeframe(data.vwap_data || []);

    const vwapData = {
      vwap: data.vwap,
      vwap_data: sampledData,
    };

    // If multi-day, update only the payload's session day and recombine
    const byDay = this.sessionByDay[sessionType];
    if (this.isMultiDay && byDay && Object.keys(byDay).length > 0) {
      byDay[data.meta.date] = vwapData;
      this.sessionData[sessionType] = this._combineMultiDayData(byDay);
    } else {
      this.sessionData[sessionType] = vwapData;
    }

    this.updateSeriesData(sessionType);
  }

  /**
   * Connect to SSE for real-time VWAP updates
   */
  _connectSSE() {
    this._disconnectSSE();

    this._sseUnsubs.push(
      unifiedSSE.subscribe('vwap', 'vwap_snapshot', (data) => {
        this._handleVwapUpdate(data);
      })
    );

    this._sseUnsubs.push(
      unifiedSSE.subscribe('vwap', 'vwap_update', (data) => {
        this._handleVwapUpdate(data);
      })
    );

    console.log('VWAP: SSE connected (unified)');
  }

  /**
   * Disconnect SSE
   */
  _disconnectSSE() {
    if (this._sseUnsubs.length > 0) {
      this._sseUnsubs.forEach(unsub => unsub());
      this._sseUnsubs = [];
      console.log('VWAP: SSE disconnected');
    }
  }

  /**
   * Draw the indicator on the chart — one set per active session.
   */
  draw() {
    if (!this.enabled) {
      return;
    }

    this.clear();

    for (const sessionType of this._activeSessions()) {
      const data = this.sessionData[sessionType];
      if (!data || !data.vwap_data || data.vwap_data.length === 0) {
        console.log(`VWAPIndicator: no ${sessionType} data to draw`);
        continue;
      }
      this._drawSet(sessionType, this._renderPoints(data));
    }
  }

  /**
   * Create the line + band series for one session and set their data.
   */
  _drawSet(sessionType, vwapDataPoints) {
    const style = SESSION_STYLES[sessionType] || SESSION_STYLES.eth;

    const line = this.chart.addSeries(LineSeries, {
      color: style.color,
      lineWidth: 2,
      title: style.title,
      priceScaleId: 'right',
      lastValueVisible: true,
      priceLineVisible: false,
      visible: true,
    });
    line.setData(this._toSeriesData(vwapDataPoints, p => p.vwap));

    const set = { line, b1u: null, b1l: null, b2u: null, b2l: null };

    // Band points: filter zero/missing band values (e.g. first point), keep gap markers
    const validBandPoints = vwapDataPoints.filter(p =>
      p.gap || (p.std_dev_1 && p.std_dev_1 !== p.vwap && p.std_dev_1_lower && p.std_dev_1_lower !== p.vwap)
    );

    if (validBandPoints.some(p => !p.gap)) {
      const bandOptions = (color, title, visible) => ({
        color,
        lineWidth: 2,
        lineStyle: 2,
        title,
        priceScaleId: 'right',
        lastValueVisible: false,
        priceLineVisible: false,
        visible,
      });

      set.b1u = this.chart.addSeries(LineSeries, bandOptions(style.band1, `${style.title} +1σ`, this.showBand1));
      set.b1l = this.chart.addSeries(LineSeries, bandOptions(style.band1, `${style.title} -1σ`, this.showBand1));
      set.b1u.setData(this._toSeriesData(validBandPoints, p => p.std_dev_1));
      set.b1l.setData(this._toSeriesData(validBandPoints, p => p.std_dev_1_lower));

      set.b2u = this.chart.addSeries(LineSeries, bandOptions(style.band2, `${style.title} +2σ`, this.showBand2));
      set.b2l = this.chart.addSeries(LineSeries, bandOptions(style.band2, `${style.title} -2σ`, this.showBand2));
      set.b2u.setData(this._toSeriesData(validBandPoints, p => p.std_dev_2));
      set.b2l.setData(this._toSeriesData(validBandPoints, p => p.std_dev_2_lower));
    }

    this.sets[sessionType] = set;
    console.log(`VWAP drawn (${sessionType}):`, {
      points: vwapDataPoints.length,
      band1: this.showBand1,
      band2: this.showBand2
    });
  }

  /**
   * Update one session's series data in place (no series re-create).
   */
  updateSeriesData(sessionType = null) {
    if (!this.enabled) return;

    const sessions = sessionType ? [sessionType] : this._activeSessions();
    for (const st of sessions) {
      const data = this.sessionData[st];
      if (!data || !data.vwap_data || data.vwap_data.length === 0) continue;

      const set = this.sets[st];
      if (!set || !set.line) {
        // Series doesn't exist yet (e.g. data arrived after enable) — full draw
        this.draw();
        return;
      }

      try {
        const pts = this._renderPoints(data);
        set.line.setData(this._toSeriesData(pts, p => p.vwap));

        const validBandPoints = pts.filter(p =>
          p.gap || (p.std_dev_1 && p.std_dev_1 !== p.vwap && p.std_dev_1_lower && p.std_dev_1_lower !== p.vwap)
        );
        if (validBandPoints.some(p => !p.gap) && set.b1u) {
          set.b1u.setData(this._toSeriesData(validBandPoints, p => p.std_dev_1));
          set.b1l.setData(this._toSeriesData(validBandPoints, p => p.std_dev_1_lower));
          set.b2u.setData(this._toSeriesData(validBandPoints, p => p.std_dev_2));
          set.b2l.setData(this._toSeriesData(validBandPoints, p => p.std_dev_2_lower));
        }
      } catch (error) {
        console.error(`VWAP: Error updating ${st} series data:`, error);
      }
    }
  }

  /**
   * Clear/remove all series from the chart
   */
  clear() {
    for (const set of Object.values(this.sets)) {
      for (const series of [set.line, set.b1u, set.b1l, set.b2u, set.b2l]) {
        if (series && this.chart) {
          try {
            this.chart.removeSeries(series);
          } catch (e) {
            // Series may already be removed or chart in invalid state
          }
        }
      }
    }
    this.sets = {};
  }

  /**
   * Enable the indicator
   *
   * @param {string} symbol - Trading symbol (e.g., 'ES')
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD), optional for multi-day mode
   * @param {string} timeframe - Timeframe for sampling (e.g., '5m')
   */
  async enable(symbol, startDate, endDate = null, timeframe = '5m') {
    console.log('VWAPIndicator: Enabling', { symbol, startDate, endDate, timeframe, mode: this.sessionMode });
    this.enabled = true;
    this.timeframe = timeframe;
    this.currentSymbol = symbol;
    this.startDate = startDate;
    this.endDate = endDate || startDate;
    this.currentDate = this.endDate;  // For real-time updates, use end date

    this.isMultiDay = !!(endDate && endDate !== startDate);
    await this._fetchAll();
    this.draw();

    // Connect SSE for real-time updates (today only)
    const today = new Date().toISOString().split('T')[0];
    if (this.currentDate === today) {
      this._connectSSE();
    }
  }

  /**
   * Disable the indicator
   */
  disable() {
    console.log('VWAPIndicator: Disabling indicator');
    this.enabled = false;
    this.clear();
    this._disconnectSSE();
  }

  /**
   * Toggle the indicator on/off
   */
  async toggle(symbol, currentDate, timeframe = '5m') {
    if (this.enabled) {
      this.disable();
    } else {
      await this.enable(symbol, currentDate, null, timeframe);
    }
    return this.enabled;
  }

  /**
   * Update the indicator with new date range
   */
  async update(symbol, startDate, endDate = null, timeframe = '5m') {
    if (!this.enabled) {
      return;
    }

    this.currentSymbol = symbol;
    this.startDate = startDate;
    this.endDate = endDate || startDate;
    this.currentDate = this.endDate;
    this.timeframe = timeframe;

    this.isMultiDay = !!(endDate && endDate !== startDate);
    await this._fetchAll();
    this.draw();
  }

  /**
   * Set 1st standard deviation band visibility (all sessions)
   */
  setBand1(show) {
    this.showBand1 = show;
    let applied = false;
    for (const set of Object.values(this.sets)) {
      if (set.b1u && set.b1l) {
        set.b1u.applyOptions({ visible: show });
        set.b1l.applyOptions({ visible: show });
        applied = true;
      }
    }
    if (!applied && show && this.enabled) {
      this.draw();
    }
  }

  /**
   * Set 2nd standard deviation band visibility (all sessions)
   */
  setBand2(show) {
    this.showBand2 = show;
    let applied = false;
    for (const set of Object.values(this.sets)) {
      if (set.b2u && set.b2l) {
        set.b2u.applyOptions({ visible: show });
        set.b2l.applyOptions({ visible: show });
        applied = true;
      }
    }
    if (!applied && show && this.enabled) {
      this.draw();
    }
  }

  /**
   * Toggle band helpers (kept for API compatibility)
   */
  toggleBand1() {
    this.setBand1(!this.showBand1);
    return this.showBand1;
  }

  toggleBand2() {
    this.setBand2(!this.showBand2);
    return this.showBand2;
  }

  /**
   * Check if indicator is enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Get current VWAP data (full-session by default)
   */
  getData(sessionType = 'eth') {
    return this.sessionData[sessionType] || null;
  }

  /**
   * Get band settings
   */
  getBandSettings() {
    return {
      band1: this.showBand1,
      band2: this.showBand2
    };
  }
}

export default VWAPIndicator;
